<template>
  <section class="contact-yellow">
    <div class="container">
      <div class="grid">

        <!-- Coluna esquerda: info + email + redes -->
        <div class="col-info">
          <h2 class="titulo">{{ $t('homePage.promoverMarca') }}</h2>

          <p class="subtitulo inter-regular">{{ $t('homePage.orcamento') }}</p>

          <div class="email-row">
            <button type="button" class="email-link" @click="copyEmail">{{ contactEmail }}</button>
            <transition name="slide-note">
              <span v-if="emailCopied" class="copied-note">✓ {{ $t('contactPage.emailCopied') }}</span>
            </transition>
          </div>

          <div class="social-section">
            <p class="social-title">{{ $t('homePage.redes-sociais') }}</p>
            <div class="social-buttons">
              <a
                v-for="social in socialLinks"
                :key="social.label"
                :href="social.url"
                target="_blank"
                rel="noopener noreferrer"
                class="social-btn"
              >
                {{ social.label }}
              </a>
            </div>
          </div>
        </div>

        <!-- Coluna direita: formulário -->
        <div class="col-form">
          <form class="contact-form" @submit.prevent="handleSubmit">
            <div class="field">
              <label class="field-label" for="contact-name">{{ $t('contactPage.formName') }}</label>
              <input
                id="contact-name"
                v-model="form.name"
                type="text"
                class="field-input"
                :placeholder="$t('contactPage.formNamePlaceholder')"
                required
              />
            </div>

            <div class="field">
              <label class="field-label" for="contact-company">{{ $t('contactPage.formCompany') }}</label>
              <input
                id="contact-company"
                v-model="form.company"
                type="text"
                class="field-input"
                :placeholder="$t('contactPage.formCompanyPlaceholder')"
              />
            </div>

            <div class="field">
              <label class="field-label" for="contact-message">{{ $t('contactPage.formMessage') }}</label>
              <textarea
                id="contact-message"
                v-model="form.message"
                class="field-input field-textarea"
                :placeholder="$t('contactPage.formMessagePlaceholder')"
                rows="5"
                required
              ></textarea>
            </div>

            <button type="submit" class="submit-btn">{{ $t('contactPage.formSend') }}</button>
          </form>
        </div>

      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, reactive } from 'vue'

const contactEmail = 'contato@gusmachado.com.br'
const emailCopied = ref(false)

const socialLinks = [
  { label: 'TikTok',    url: 'https://www.tiktok.com/@_gusmachado' },
  { label: 'Instagram', url: 'https://www.instagram.com/_gusmachado' },
  { label: 'YouTube',   url: 'https://www.youtube.com/channel/UCeJ7fXHX5sl8hLSJufdjiAw' },
  { label: 'Facebook',  url: 'https://www.facebook.com/gusmontanharussa' },
]

const form = reactive({ name: '', company: '', message: '' })

const copyEmail = async () => {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(contactEmail)
    } else {
      const temp = document.createElement('textarea')
      temp.value = contactEmail
      temp.setAttribute('readonly', '')
      temp.style.cssText = 'position:absolute;left:-9999px'
      document.body.appendChild(temp)
      temp.select()
      document.execCommand('copy')
      document.body.removeChild(temp)
    }
    emailCopied.value = true
    setTimeout(() => { emailCopied.value = false }, 2200)
  } catch {
    emailCopied.value = false
  }
}

const handleSubmit = () => {
  const subject = encodeURIComponent(
    form.company
      ? `Orçamento - ${form.company} (${form.name})`
      : `Orçamento - ${form.name}`
  )
  const body = encodeURIComponent(
    `Nome: ${form.name}\nEmpresa: ${form.company || '-'}\n\nMensagem:\n${form.message}`
  )
  window.location.href = `mailto:${contactEmail}?subject=${subject}&body=${body}`
}
</script>

<style scoped>
.contact-yellow {
  min-height: 100%;
  background: var(--color-yellow);
  color: var(--color-bg-dark);
  overflow-y: auto;
  display: flex;
  align-items: center;
}

.container {
  max-width: 1100px;
  width: 100%;
  margin: 0 auto;
  padding: 52px 24px 64px;
}

/* ─── Grid ────────────────────────────────────────── */
.grid {
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 72px;
  align-items: center;
}

/* ─── Coluna info ─────────────────────────────────── */
.titulo {
  margin: 0;
  font-family: "Bebas Neue", sans-serif;
  font-size: 52px;
  font-weight: 400;
  line-height: 1.05;
  hyphens: none;
  word-break: keep-all;
  overflow-wrap: normal;
}

.subtitulo {
  margin: 20px 0 0;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.6;
  opacity: 0.6;
  letter-spacing: 0.01em;
  text-align: center;
}

/* ─── Email ───────────────────────────────────────── */
.email-row {
  margin: 14px 0 36px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.email-link {
  font-family: "Archivo", sans-serif;
  font-size: 17px;
  font-weight: 800;
  color: var(--color-bg-dark);
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  transition: opacity 0.2s ease;
}

.email-link:hover {
  opacity: 0.55;
}

.copied-note {
  font-family: "Archivo", sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  color: var(--color-bg-dark);
  text-transform: uppercase;
}

/* animação: sobe e aparece */
.slide-note-enter-active {
  animation: pop-up 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.slide-note-leave-active {
  transition: opacity 0.25s ease;
}
.slide-note-leave-to {
  opacity: 0;
}

@keyframes pop-up {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ─── Redes sociais ───────────────────────────────── */
.social-title {
  margin: 0 0 14px;
  font-family: "Archivo", sans-serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  opacity: 0.5;
}

.social-buttons {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.social-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 42px;
  border: 1.5px solid rgba(30, 42, 58, 0.28);
  border-radius: 0;
  color: var(--color-bg-dark);
  text-decoration: none;
  font-family: "Archivo", sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  transition: background 0.22s ease, color 0.22s ease, border-color 0.22s ease;
}

.social-btn:hover {
  background: var(--color-bg-dark);
  color: var(--color-yellow);
  border-color: var(--color-bg-dark);
}

/* ─── Formulário ──────────────────────────────────── */
.field {
  margin-bottom: 30px;
}

.field-label {
  display: block;
  font-family: "Archivo", sans-serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  margin-bottom: 12px;
  opacity: 0.55;
}

.field-input {
  width: 100%;
  background: transparent;
  border: none;
  border-bottom: 2px solid rgba(30, 42, 58, 0.22);
  border-radius: 0;
  padding: 9px 0;
  font-family: "Inter", sans-serif;
  font-size: 15px;
  font-weight: 500;
  color: var(--color-bg-dark);
  outline: none;
  transition: border-color 0.25s ease;
  box-sizing: border-box;
  -webkit-appearance: none;
}

.field-input:focus {
  border-color: var(--color-bg-dark);
}

.field-input::placeholder {
  color: rgba(30, 42, 58, 0.28);
  font-weight: 400;
}

.field-textarea {
  resize: none;
  min-height: 96px;
  font-family: "Inter", sans-serif;
}

.submit-btn {
  width: 100%;
  padding: 16px;
  background: var(--color-bg-dark);
  color: var(--color-yellow);
  border: 2px solid var(--color-bg-dark);
  border-radius: 0;
  font-family: "Archivo", sans-serif;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.25s ease, color 0.25s ease;
  margin-top: 10px;
}

.submit-btn:hover {
  background: transparent;
  color: var(--color-bg-dark);
}

/* ─── Mobile ──────────────────────────────────────── */
@media (aspect-ratio <= 3/4) {
  .container {
    padding: 40px 20px 56px;
  }

  .grid {
    grid-template-columns: 1fr;
    gap: 40px;
    align-items: start;
  }

  .titulo {
    font-size: 42px;
  }

  .divider {
    margin: 16px 0;
  }

  .email-link {
    font-size: 14px;
  }

  .social-buttons {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
